<template>
  <div class="file-attachment">
    <div class="file-attachment__card">
      <div class="file-attachment__icon-wrapper">
        <Icon :icon="getFileIcon(attachment.mimetype)" class="file-attachment__icon" />
      </div>
      <div class="file-attachment__content">
        <div class="file-attachment__info">
          <span class="file-attachment__name">{{
            attachment.file_name || attachment.name || "File"
          }}</span>
          <span
            v-if="
              attachment.file_size ||
              (typeof attachment.size === 'number' ? attachment.size : undefined)
            "
            class="file-attachment__size"
          >
            {{
              formatFileSize(
                attachment.file_size ||
                  (typeof attachment.size === "number" ? attachment.size : undefined)
              )
            }}
          </span>
        </div>
      </div>
      <button
        class="file-attachment__download"
        @click.stop="handleDownload"
        :disabled="isDownloading"
        title="Download file"
      >
        <ProgressCircle
          v-if="isDownloading && loadingProgress > 0"
          :progress="loadingProgress"
          size="small"
          bg-stroke="rgba(0, 0, 0, 0.1)"
          fill-stroke="#111827"
          :show-text="false"
        />
        <Icon v-else-if="isDownloading" icon="mdi:loading" class="file-attachment__spinner" />
        <Icon v-else icon="mdi:download" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentDownload } from "@/composables/attachments/useAttachmentDownload";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}

const props = defineProps<Props>();

const { isDownloading, isLoading, loadingProgress, download } = useAttachmentDownload(
  props.attachment,
  props.messageId
);

const handleDownload = () => {
  download("application/octet-stream", `attachment_${props.attachment.id}`);
};

const getFileIcon = (mimetype?: string): string => {
  if (!mimetype) return "mdi:file";
  if (mimetype.startsWith("image/")) return "mdi:file-image";
  if (mimetype.startsWith("video/")) return "mdi:file-video";
  if (mimetype.startsWith("audio/")) return "mdi:file-music";
  if (mimetype.includes("pdf")) return "mdi:file-pdf-box";
  if (mimetype.includes("word") || mimetype.includes("document")) return "mdi:file-word";
  if (mimetype.includes("excel") || mimetype.includes("spreadsheet")) return "mdi:file-excel";
  if (mimetype.includes("powerpoint") || mimetype.includes("presentation"))
    return "mdi:file-powerpoint";
  if (mimetype.includes("zip") || mimetype.includes("archive")) return "mdi:folder-zip";
  return "mdi:file";
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
</script>

<style scoped>
.file-attachment {
  position: relative;
  max-width: 300px;
}

.file-attachment__card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.file-attachment__card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.file-attachment__icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 10px;
  flex-shrink: 0;
}

.file-attachment__icon {
  font-size: 1.25rem;
  color: #4b5563;
}

.file-attachment__content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.file-attachment__info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}

.file-attachment__name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-attachment__size {
  font-size: 0.75rem;
  color: #6b7280;
}

.file-attachment__download {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: #6b7280;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
}

.file-attachment__download:hover:not(:disabled) {
  background: #e5e7eb;
  color: #111827;
  transform: scale(1.05);
}

.file-attachment__download:active:not(:disabled) {
  transform: scale(0.95);
}

.file-attachment__download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.file-attachment__spinner {
  font-size: 1.25rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
